import { StyledMansLauksDrawer } from './style';
import { ProductData } from '../../../types/ProductData';
import { Table } from '../../Table';
import { useIntl } from 'react-intl';
import { Select, SelectOption } from '../../Select';
import { Input } from '../../Input';
import { Form, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../Button';
import { useForm } from 'antd/lib/form/Form';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import useFormValidation from '../../../utils/useFormValidation';
import { Icon } from 'ui';
import useMarketType from '../../../utils/useMarketType';

export interface MansLauksProduct {
  id: string;
  name: string;
  unit: string;
  bio: boolean;
  amount: number;
}

export const mansLauksPairKey = (pair: Pick<MansLauksProduct, 'id' | 'bio'>) =>
  `${pair.id}|${pair.bio ? 'bio' : 'non-bio'}`;

interface Props {
  products: ProductData[];
  mansLauksProducts: MansLauksProduct[];
  open: boolean;
  onClose: () => void;
  getMansLauksProducts: (
    data?: any,
    urlParams?: any,
    passOnSuccess?: any
  ) => void;
  setReload: React.Dispatch<React.SetStateAction<number>>;
  farmId?: number;
}

export const DrawerMansLauksProducts = ({
  products,
  mansLauksProducts,
  open,
  onClose,
  getMansLauksProducts,
  setReload,
  farmId,
}: Props) => {
  const [selectedMansLauks, setSelectedMansLauks] = useState<{
    [key: string]: MansLauksProduct | undefined;
  }>({});
  const [missingPairs, setMissingPairs] = useState<MansLauksProduct[]>([]);
  const intl = useIntl();
  const [form] = useForm();
  const { isEmarket } = useMarketType();

  const { nogaStorageStockCountNumberCheck } = useFormValidation();

  const pairOptions = useMemo(
    () => [...mansLauksProducts, ...missingPairs],
    [mansLauksProducts, missingPairs]
  );

  useEffect(() => {
    if (open) {
      const nextSelected: { [key: number]: MansLauksProduct | undefined } = {};
      const nextFormValues: Record<string, any> = {};
      const nextMissing: MansLauksProduct[] = [];

      products.forEach((product) => {
        if (
          !product.manslauks_id ||
          typeof product.manslauks_bio !== 'boolean'
        ) {
          return;
        }

        const productAmount = product?.product_amounts?.find((amount) =>
          isEmarket ? amount.type === 'EMARKET' : amount.type !== 'EMARKET'
        );
        const mapped = { id: product.manslauks_id, bio: product.manslauks_bio };
        let pair = mansLauksProducts.find(
          (mlProd) => mansLauksPairKey(mlProd) === mansLauksPairKey(mapped)
        );

        // ML omits inactive products; keep the saved pair selectable so saving doesn't drop it
        if (!pair) {
          pair = {
            ...mapped,
            name: product.name ?? '',
            unit: productAmount?.storage_unit ?? '',
            amount: 0,
          };
          nextMissing.push(pair);
        }

        nextSelected[product.id] = pair;
        nextFormValues[`mans_lauks_product_${product.id}`] =
          mansLauksPairKey(pair);

        if (productAmount?.storage_stock_count !== null) {
          nextFormValues[`noga_storage_stock_count_${product.id}`] =
            productAmount?.storage_stock_count;
        }
      });

      setMissingPairs(nextMissing);
      setSelectedMansLauks(nextSelected);

      form.setFieldsValue(nextFormValues);
    }
  }, [open, products, mansLauksProducts, form, isEmarket]);

  const { appendData } = useQueryApiClient({
    request: {
      url: `/api/v2/manslauks/${farmId}/products`,
      method: 'POST',
    },
    onSuccess: () => {
      return message.success(
        intl.formatMessage({ id: 'message.successful_update' })
      );
    },
    onError: (error) => {
      message.error(
        error?.error ??
          intl.formatMessage({ id: 'message.manslauks_mapping_failed' })
      );
    },
    onFinally() {
      getMansLauksProducts();
      setReload((old) => old + 1);
    },
  });

  const onSelectMansLauksProduct = (
    value: string | undefined,
    productId: number
  ) => {
    const selectedProd = pairOptions.find(
      (pair) => mansLauksPairKey(pair) === value
    );
    setSelectedMansLauks((prev) => ({
      ...prev,
      [productId]: selectedProd,
    }));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      const updates = products
        .map((product) => {
          const nogaStorageStockCount =
            values[`noga_storage_stock_count_${product.id}`];
          const selectedMlProduct = selectedMansLauks[product.id];
          if (!nogaStorageStockCount || !selectedMlProduct) {
            return null;
          }

          return {
            product_id: product.id,
            manslauks_count: selectedMlProduct.amount,
            manslauks_unit: selectedMlProduct.unit,
            noga_storage_stock_count: nogaStorageStockCount,
            noga_unit: values[`noga_unit_${product.id}`],
            manslauks_id: selectedMlProduct.id,
            manslauks_bio: selectedMlProduct.bio,
          };
        })
        .filter(Boolean);

      const deletes = products
        .filter(
          (product) => product.manslauks_id && !selectedMansLauks[product.id]
        )
        .map((product) => ({
          delete: true,
          product_id: product.id,
          manslauks_count: null,
          manslauks_unit: null,
          noga_storage_stock_count: 0,
          noga_unit: '',
          manslauks_id: product.manslauks_id,
        }));

      appendData([...updates, ...deletes]);
      onClose();
    } catch (err: unknown) {
      message.error(intl.formatMessage({ id: 'message.fix_errors' }));
    }
  };

  const RenderMansLauksSelector = (record: ProductData) => {
    const takenByOtherProducts = Object.entries(selectedMansLauks).flatMap(
      ([productId, pair]) =>
        pair && Number(productId) !== record.id ? [mansLauksPairKey(pair)] : []
    );
    const options = pairOptions.filter(
      (pair) => !takenByOtherProducts.includes(mansLauksPairKey(pair))
    );
    const current = selectedMansLauks[record.id];

    return (
      <Select
        name={`mans_lauks_product_${record.id}`}
        allowClear
        showSearch
        onChange={(value: any) => onSelectMansLauksProduct(value, record.id)}
        value={current ? mansLauksPairKey(current) : undefined}
        getPopupContainer={() => document.body}
      >
        {options.map((pair) => (
          <SelectOption
            key={mansLauksPairKey(pair)}
            value={mansLauksPairKey(pair)}
          >
            {`${pair.name} — ${intl.formatMessage({
              id: pair.bio ? 'manslauks.bio' : 'manslauks.not_bio',
            })} — ${pair.amount} ${pair.unit}`}
          </SelectOption>
        ))}
      </Select>
    );
  };

  const RenderMansLauksStorageStockCount = (record: MansLauksProduct) => {
    const selectedProd = selectedMansLauks[record.id];
    if (!selectedProd) return null;

    return (
      <div className={'unit-value'}>
        <div className={'value'}>{selectedProd.amount}</div>
        <div className={'unit'}>{selectedProd.unit}</div>
      </div>
    );
  };

  const RenderNogaStorageStockCount = (record: MansLauksProduct) => {
    const selectedProd = selectedMansLauks[record.id];
    if (!selectedProd) return null;

    return (
      <div className={'storage-unit'}>
        <div>
          <Input
            name={`noga_storage_stock_count_${record.id}`}
            type={'number'}
            rules={[
              { validator: nogaStorageStockCountNumberCheck(selectedProd) },
            ]}
          />
        </div>
        <div>
          <Select
            name={'noga_unit'}
            initialValue={selectedProd.unit}
            getPopupContainer={() => document.body}
          >
            <SelectOption>{selectedProd.unit}</SelectOption>
          </Select>
        </div>
      </div>
    );
  };

  const Footer = () => {
    return (
      <Button
        type={'primary'}
        label={intl.formatMessage({ id: 'general.save' })}
        onClick={handleSubmit}
      />
    );
  };

  const columns = [
    {
      title: intl.formatMessage({ id: 'general.noga_products' }),
      dataIndex: 'name',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'general.mans_lauks_products' }),
      render: (record: ProductData) => RenderMansLauksSelector(record),
    },
    {
      title: intl.formatMessage({
        id: 'general.mans_lauks_storage_stock_count',
      }),
      render: (record: MansLauksProduct) =>
        RenderMansLauksStorageStockCount(record),
    },
    {
      title: intl.formatMessage({ id: 'general.noga_storage_stock_count' }),
      render: (record: MansLauksProduct) => RenderNogaStorageStockCount(record),
    },
  ];

  return (
    <StyledMansLauksDrawer
      open={open}
      onClose={onClose}
      width={1000}
      footer={<Footer />}
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <Form form={form}>
        <Table columns={columns} dataSource={products} />
      </Form>
    </StyledMansLauksDrawer>
  );
};
